import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, shareReplay, catchError, map } from 'rxjs';

export interface GravatarResponse {
	url: string;
	email: string;
	cached: boolean;
	exists: boolean;
}

export interface GravatarOptions {
	size?: number;
	default?: 'identicon';
}

@Injectable({
	providedIn: 'root'
})
export class GravatarService {
	private readonly http = inject(HttpClient);
	private readonly baseUrl = '/api/gravatar';

	// In-memory cache for already fetched avatars
	private readonly avatarCache = new Map<string, Observable<GravatarResponse>>();

	/**
	 * Get Gravatar info (URL + exists status) from backend
	 */
	getGravatarInfo(email: string, options?: GravatarOptions): Observable<GravatarResponse> {
		if (!email) {
			return of({
				url: this.getDefaultAvatarUrl(),
				email: '',
				cached: false,
				exists: false
			});
		}

		const cacheKey = this.generateCacheKey(email, options);

		// Return cached observable if available
		if (this.avatarCache.has(cacheKey)) {
			return this.avatarCache.get(cacheKey)!;
		}

		// Create new request and cache it
		const request$ = this.fetchGravatarInfo(email, options).pipe(
			catchError(error => {
				console.error('Error fetching Gravatar:', error);
				return of({
					url: this.getDefaultAvatarUrl(),
					email,
					cached: false,
					exists: false
				});
			}),
			shareReplay(1) // Share the result with multiple subscribers
		);

		this.avatarCache.set(cacheKey, request$);
		return request$;
	}

	/**
	 * Get Gravatar URL only (for backward compatibility)
	 */
	getGravatarUrl(email: string, options?: GravatarOptions): Observable<string> {
		return this.getGravatarInfo(email, options).pipe(
			map(response => response.url)
		);
	}

	/**
	 * Get Gravatar URLs for multiple emails
	 */
	getGravatarUrlsBatch(emails: string[], options?: GravatarOptions): Observable<Record<string, GravatarResponse>> {
		const body = {
			emails,
			size: options?.size?.toString() || '200',
			default: options?.default || 'identicon'
		};

		return this.http.post<Record<string, GravatarResponse>>(`${this.baseUrl}/batch`, body).pipe(
			catchError(error => {
				console.error('Error fetching Gravatar batch:', error);
				return of({});
			})
		);
	}

	/**
	 * Clear cache for specific email
	 */
	clearCache(email?: string): void {
		if (email) {
			// Clear specific email from cache
			const keysToDelete = Array.from(this.avatarCache.keys()).filter(key =>
				key.startsWith(`${email}:`)
			);
			keysToDelete.forEach(key => this.avatarCache.delete(key));
		} else {
			// Clear entire cache
			this.avatarCache.clear();
		}
	}

	/**
	 * Prefetch avatar for better performance
	 */
	prefetchAvatar(email: string, options?: GravatarOptions): void {
		this.getGravatarInfo(email, options).subscribe();
	}

	/**
	 * Get Gravatar profile information
	 */
	getGravatarProfile(email: string): Observable<any> {
		const params = new HttpParams().set('email', email);

		return this.http.get<any>(`${this.baseUrl}/profile`, { params }).pipe(
			catchError(error => {
				console.error('Error fetching Gravatar profile:', error);
				return of(null);
			})
		);
	}

	private fetchGravatarInfo(email: string, options?: GravatarOptions): Observable<GravatarResponse> {
		let params = new HttpParams().set('email', email);

		if (options?.size) {
			params = params.set('size', options.size.toString());
		}

		if (options?.default) {
			params = params.set('default', options.default);
		}

		return this.http.get<GravatarResponse>(this.baseUrl, { params });
	}

	private generateCacheKey(email: string, options?: GravatarOptions): string {
		const size = options?.size || 200;
		const defaultStyle = options?.default || 'identicon';
		return `${email}:${size}:${defaultStyle}`;
	}

	private getDefaultAvatarUrl(): string {
		// Simple SVG placeholder
		return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2NjYyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjgwIiBmaWxsPSIjZmZmIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Pz88L3RleHQ+PC9zdmc+';
	}
}
